import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { COUNTRIES } from '../../constants/countries';

/**
 * CountryStep — Country selection dropdown during registration.
 *
 * @param {Object} props
 * @param {string} props.country - Currently selected country
 * @param {function} props.onCountryChange - Called with new country value
 * @param {function} props.onContinue - Called when country is confirmed
 * @param {function} props.onBack - Called to go back
 * @returns {JSX.Element}
 */
function CountryStep({ country, onCountryChange, onContinue, onBack }) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filteredCountries = useMemo(() => {
    if (!search.trim()) return COUNTRIES;
    return COUNTRIES.filter((c) =>
      c.toLowerCase().includes(search.toLowerCase())
    );
  }, [search]);

  const handleSelect = (selected) => {
    onCountryChange(selected);
    setSearch('');
    setIsOpen(false);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!country) return;
    console.debug('[CountryStep] submit', { country });
    onContinue(country);
  };

  return (
    <div className="auth-step country-step">
      <button type="button" className="auth-step__back" onClick={onBack} aria-label="Go back">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5" />
          <path d="M12 19l-7-7 7-7" />
        </svg>
      </button>

      <h2 className="auth-step__title">Where are you from?</h2>
      <p className="auth-step__subtitle">Select your country</p>

      <form onSubmit={handleSubmit} className="auth-step__form">
        <div className="auth-input-group">
          <label className="auth-input__label">Country</label>
          <div className="country-step__dropdown">
            <input
              type="text"
              className="auth-input country-step__search"
              placeholder={country || 'Search or select a country...'}
              value={search}
              onChange={handleSearchChange}
              onFocus={() => setIsOpen(true)}
              autoFocus
              autoComplete="off"
            />
            {country && !isOpen && (
              <div className="country-step__selected">
                <span>{country}</span>
                <button
                  type="button"
                  className="country-step__clear"
                  onClick={() => { onCountryChange(''); setIsOpen(true); }}
                  aria-label="Clear selection"
                >
                  &times;
                </button>
              </div>
            )}
            {isOpen && (
              <ul className="country-step__list" role="listbox">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((c) => (
                    <li
                      key={c}
                      role="option"
                      aria-selected={c === country}
                      className={`country-step__option ${c === country ? 'country-step__option--selected' : ''}`}
                      onClick={() => handleSelect(c)}
                    >
                      {c}
                    </li>
                  ))
                ) : (
                  <li className="country-step__no-results">No countries found</li>
                )}
              </ul>
            )}
          </div>
        </div>

        <button
          type="submit"
          className="auth-continue-btn"
          disabled={!country}
        >
          Continue
        </button>
      </form>
    </div>
  );
}

CountryStep.propTypes = {
  country: PropTypes.string.isRequired,
  onCountryChange: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default CountryStep;
